import React, { useEffect, useState } from 'react';
import {
  candidatesAPI,
  interviewsAPI,
  emailsAPI,
  interviewersAPI
} from '../api/client';
import { toast } from 'react-toastify';
import '../pages/AnimationsAndStyles.css';

/* ================= CONSTANTS ================= */

const POSITIONS = [
  'All',
  'Embedded System',
  'Embedded Software',
  'Post Silicon Validation',
  'VLSI',
  'Others',
  'HR',
  'Business Unit'
];

/* ================= COMPONENT ================= */

export default function InterviewsList() {

  /* ================= STATES ================= */

  const [candidates, setCandidates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('All');

  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [interviewers, setInterviewers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showInterviewerModal, setShowInterviewerModal] = useState(false);

  const [newInterviewer, setNewInterviewer] = useState({
    name: '',
    email: '',
    role: '',
    department: ''
  });

  const [form, setForm] = useState({
    scheduled_date: '',
    interview_type: 'Technical',
    interviewer_name: '',
    interviewer_email: '',
    interviewer_role: '',
    interviewer_department: ''
  });

  /* ================= LOAD ================= */

  useEffect(() => {
    fetchCandidates();
    fetchInterviewers();
  }, []);

  /* ================= API ================= */

  const fetchCandidates = async () => {
    try {
      const res = await candidatesAPI.getAll();
      setCandidates(res.data);
      setFiltered(res.data);
    } catch {
      toast.error('Failed to fetch candidates');
    }
  };

  const fetchInterviewers = async () => {
    try {
      const res = await interviewersAPI.getAll();
      setInterviewers(res.data);
    } catch {
      toast.error('Failed to load interviewers');
    }
  };

  /* ================= FILTER ================= */

  const filterByPosition = () => {
    if (selectedPosition === 'All') {
      setFiltered(candidates);
      return;
    }

    setFiltered(
      candidates.filter(c =>
        (c.position || '').toLowerCase().includes(selectedPosition.toLowerCase())
      )
    );
  };

  useEffect(() => {
    filterByPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPosition, candidates]);

  /* ================= SCHEDULE ================= */

  const openModal = (candidate) => {
    setSelectedCandidate(candidate);
    setForm({
      scheduled_date: '',
      interview_type: 'Technical',
      interviewer_name: '',
      interviewer_email: '',
      interviewer_role: '',
      interviewer_department: ''
    });
    setShowModal(true);
  };

  const scheduleInterview = async () => {
    const {
      scheduled_date,
      interviewer_name,
      interviewer_department
    } = form;

    if (!scheduled_date || !interviewer_name || !interviewer_department) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      // 1️⃣ Create interview
      const interviewRes = await interviewsAPI.create({
        candidate_id: selectedCandidate.id,
        scheduled_date: form.scheduled_date,
        interview_type: form.interview_type,
        interviewer_name: form.interviewer_name,
        interviewer_email: form.interviewer_email,
        interviewer_role: form.interviewer_role,
        interviewer_department: form.interviewer_department
      });

      const interviewId = interviewRes.data.id;

      // 2️⃣ Send email (NO undefined now)
      await emailsAPI.sendInterview({
        candidate_email: selectedCandidate.email_id,
        interview_date: form.scheduled_date,
        interview_type: form.interview_type,
        interviewer: form.interviewer_name
      });

      // 3️⃣ Update status
      await interviewsAPI.updateStatus(interviewId, {
        status: 'invitation_sent'
      });

      // 🔁 REFRESH UI DATA
      await fetchCandidates();

      toast.success('Interview scheduled & invitation sent');
      setShowModal(false);

    } catch (err) {
      console.error(err);
      toast.error('Interview scheduled but email failed');
    }
  };

  /* ================= INTERVIEWERS ================= */

  // eslint-disable-next-line no-unused-vars
  const addInterviewer = async () => {
    const { name, email, role, department } = newInterviewer;

    if (!name || !role || !department) {
      toast.error('Please fill required fields');
      return;
    }

    await interviewersAPI.create({ name, email, role, department });

    setNewInterviewer({ name: '', email: '', role: '', department: '' });
    fetchInterviewers();
    toast.success('Interviewer added');
  };

  // eslint-disable-next-line no-unused-vars
  const deleteInterviewer = async (id) => {
    await interviewersAPI.delete(id);
    fetchInterviewers();
    toast.success('Interviewer removed');
  };

  /* ================= UI ================= */

  return (
    <div style={styles.page}>

      {/* HEADER */}
      <div style={styles.header}>
        <h2>Interviews</h2>

        <div>
          <select
            style={styles.select}
            value={selectedPosition}
            onChange={e => setSelectedPosition(e.target.value)}
          >
            {POSITIONS.map(p => <option key={p}>{p}</option>)}
          </select>

          <button
            style={styles.manageBtn}
            onClick={() => setShowInterviewerModal(true)}
          >
            Manage Interviewers
          </button>
        </div>
      </div>

      {/* TABLE */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Position</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(c => (
            <tr key={c.id}>
              <td>{c.first_name} {c.last_name}</td>
              <td>{c.position}</td>
              <td>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  color: '#fff',
                  background:
                    c.status === 'invitation_sent' ? '#28a745' :
                    c.status === 'scheduled' ? '#ffc107' : '#6c757d'
                }}>
                  {c.status}
                </span>
              </td>
              <td>
                <button style={styles.button} onClick={() => openModal(c)}>
                  Schedule Interview
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* MODAL */}
      {showModal && (
        <div style={styles.backdrop}>
          <div style={styles.modal}>

            <h3>Schedule Interview</h3>

            <input
              type="datetime-local"
              style={styles.input}
              onChange={e => setForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
            />

            <select
              style={styles.input}
              onChange={e => setForm(prev => ({ ...prev, interview_type: e.target.value }))}
            >
              <option>Technical</option>
              <option>Non-Technical</option>
            </select>

            <select
              style={styles.input}
              onChange={e => setForm(prev => ({ ...prev, interviewer_department: e.target.value }))}
            >
              <option value="">Select Department</option>
              {POSITIONS.filter(p => p !== 'All').map(p => (
                <option key={p}>{p}</option>
              ))}
            </select>

            <select
              style={styles.input}
              onChange={e => {
                const interviewer = interviewers.find(i => i.id === Number(e.target.value));
                if (interviewer) {
                  setForm(prev => ({
                    ...prev,
                    interviewer_name: interviewer.name,
                    interviewer_email: interviewer.email,
                    interviewer_role: interviewer.role,
                    interviewer_department: interviewer.department
                  }));
                }
              }}
            >
              <option value="">Select Interviewer</option>
              {interviewers.map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>

            <button style={styles.confirmBtn} onClick={scheduleInterview}>Confirm</button>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  page: { padding: 20 },
  header: { display: 'flex', justifyContent: 'space-between' },
  select: { padding: 6, marginRight: 10 },
  manageBtn: { padding: '6px 12px', background: '#ff9800', color: '#fff', border: 'none' },
  table: { width: '100%', marginTop: 10, borderCollapse: 'collapse' },
  button: { background: '#007bff', color: '#fff', padding: '6px 10px', border: 'none' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)' },
  modal: { background: '#fff', padding: 20, width: 420, margin: 'auto' },
  input: { width: '100%', padding: 6, marginBottom: 8 },
  confirmBtn: { background: '#28a745', color: '#fff', padding: 6, border: 'none' },
  cancelBtn: { background: '#6c757d', color: '#fff', padding: 6, border: 'none' }
};

// import React, { useEffect, useState } from 'react';
// import {
//   candidatesAPI,
//   interviewsAPI,
//   emailsAPI,
//   interviewersAPI
// } from '../api/client';
// import { toast } from 'react-toastify';

// /* ================= CONSTANTS ================= */

// const POSITIONS = [
//   'All',
//   'Embedded System',
//   'Embedded Software',
//   'Post Silicon Validation',
//   'VLSI',
//   'Others',
//   'HR',
//   'Business Unit'
// ];

// /* ================= COMPONENT ================= */

// export default function InterviewsList() {
//   /* ================= STATES ================= */

//   // Candidates
//   const [candidates, setCandidates] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [selectedPosition, setSelectedPosition] = useState('All');

//   // Schedule Interview Modal
//   const [showModal, setShowModal] = useState(false);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Interviewers
//   const [interviewers, setInterviewers] = useState([]);
//   const [showInterviewerModal, setShowInterviewerModal] = useState(false);

//   // Add Interviewer Form
//   const [newInterviewer, setNewInterviewer] = useState({
//     name: '',
//     email: '',
//     role: '',
//     department: ''
//   });

//   // Schedule Interview Form (❌ interviewer_id removed)
//   const [form, setForm] = useState({
//     scheduled_date: '',
//     interview_type: 'Technical',
//     interviewer_name: '',
//     interviewer_email: '',
//     interviewer_role: '',
//     interviewer_department: ''
//   });

//   /* ================= LOAD ================= */

//   useEffect(() => {
//     fetchCandidates();
//     fetchInterviewers();
//   }, []);

//   useEffect(() => {
//     filterByPosition();
//   }, [selectedPosition, candidates]);

//   /* ================= API CALLS ================= */

//   const fetchCandidates = async () => {
//     try {
//       const res = await candidatesAPI.getAll();
//       setCandidates(res.data);
//       setFiltered(res.data);
//     } catch {
//       toast.error('Failed to fetch candidates');
//     }
//   };

//   const fetchInterviewers = async () => {
//     try {
//       const res = await interviewersAPI.getAll();
//       setInterviewers(res.data);
//     } catch {
//       toast.error('Failed to load interviewers');
//     }
//   };

//   /* ================= FILTER ================= */

//   const filterByPosition = () => {
//     if (selectedPosition === 'All') {
//       setFiltered(candidates);
//       return;
//     }

//     setFiltered(
//       candidates.filter(c =>
//         (c.position || '')
//           .toLowerCase()
//           .includes(selectedPosition.toLowerCase())
//       )
//     );
//   };

//   /* ================= SCHEDULE INTERVIEW ================= */

//   const openModal = (candidate) => {
//     setSelectedCandidate(candidate);
//     setForm({
//       scheduled_date: '',
//       interview_type: 'Technical',
//       interviewer_name: '',
//       interviewer_email: '',
//       interviewer_role: '',
//       interviewer_department: ''
//     });
//     setShowModal(true);
//   };

//   // const scheduleInterview = async () => {
//   //   const {
//   //     scheduled_date,
//   //     interviewer_name,
//   //     interviewer_department
//   //   } = form;

//   //   if (!scheduled_date || !interviewer_name || !interviewer_department) {
//   //     toast.error('Please fill all required fields');
//   //     return;
//   //   }

//   //   try {
//   //     const interviewRes = await interviewsAPI.create({
//   //       candidate_id: selectedCandidate.id,
//   //       scheduled_date: form.scheduled_date,
//   //       interview_type: form.interview_type,
//   //       interviewer_name: form.interviewer_name,
//   //       interviewer_email: form.interviewer_email,
//   //       interviewer_role: form.interviewer_role,
//   //       interviewer_department: form.interviewer_department
//   //     });

//   //     await emailsAPI.sendInterview({
//   //       candidate_email: selectedCandidate.email_id,
//   //       candidate_id: selectedCandidate.id,
//   //       interview_id: interviewRes.data.id,
//   //       email_service: 'gmail'
//   //     });

//   //     toast.success('Interview scheduled successfully');
//   //     setShowModal(false);
//   //   } catch (err) {
//   //     console.error(err);
//   //     toast.error('Scheduling failed');
//   //   }
//   // };
// const scheduleInterview = async () => {
//   if (!form.scheduled_date || !form.interviewer_name || !form.interviewer_department) {
//     toast.error('Please fill all required fields');
//     return;
//   }

//   try {
//     // 1️⃣ Create interview
//     const interviewRes = await interviewsAPI.create({
//       candidate_id: selectedCandidate.id,
//       scheduled_date: form.scheduled_date,
//       interview_type: form.interview_type,
//       interviewer_name: form.interviewer_name,
//       interviewer_email: form.interviewer_email,
//       interviewer_role: form.interviewer_role,
//       interviewer_department: form.interviewer_department
//     });

//     const interviewId = interviewRes.data.id;

//     // 2️⃣ Send email
//     // await emailsAPI.sendInterview({
//     //   candidate_email: selectedCandidate.email_id,
//     //   candidate_id: selectedCandidate.id,
//     //   interview_id: interviewId,
//     //   email_service: 'gmail'
//     // });
//     await emailsAPI.sendInterview({
//   candidate_email: selectedCandidate.email_id,
//   interview_date: form.scheduled_date,
//   interview_type: form.interview_type,
//   interviewer: selectedInterviewer.name
// });

//     // 3️⃣ Update status ONLY if email succeeds
//     await interviewsAPI.updateStatus(interviewId, {
//       status: 'invitation_sent'
//     });

//     toast.success('Interview scheduled & invitation sent');
//     setShowModal(false);

//   } catch (err) {
//     console.error(err);
//     toast.error('Interview scheduled but email failed');
//   }
// };

//   /* ================= INTERVIEWER MANAGEMENT ================= */

//   const addInterviewer = async () => {
//     const { name, email, role, department } = newInterviewer;

//     if (!name || !role || !department) {
//       toast.error('Please fill required fields');
//       return;
//     }

//     await interviewersAPI.create({
//       name,
//       email,
//       role,
//       department
//     });

//     setNewInterviewer({
//       name: '',
//       email: '',
//       role: '',
//       department: ''
//     });

//     fetchInterviewers();
//     toast.success('Interviewer added');
//   };

//   const deleteInterviewer = async (id) => {
//     await interviewersAPI.delete(id);
//     fetchInterviewers();
//     toast.success('Interviewer removed');
//   };

//   /* ================= UI ================= */

//   return (
//     <div style={styles.page}>
//       {/* HEADER */}
//       <div style={styles.header}>
//         <h2>Interviews</h2>

//         <div>
//           <select
//             style={styles.select}
//             value={selectedPosition}
//             onChange={e => setSelectedPosition(e.target.value)}
//           >
//             {POSITIONS.map(p => (
//               <option key={p}>{p}</option>
//             ))}
//           </select>

//           <button
//             style={styles.manageBtn}
//             onClick={() => setShowInterviewerModal(true)}
//           >
//             Manage Interviewers
//           </button>
//         </div>
//       </div>

//       {/* CANDIDATES TABLE */}
//       <table style={styles.table}>
//         <thead>
//           <tr>
//             <th>Name</th>
//             <th>Position</th>
//             <th>Status</th>
//             <th>Action</th>
//           </tr>
//         </thead>
//         <tbody>
//           {filtered.map(c => (
//             <tr key={c.id}>
//               <td>{c.first_name} {c.last_name}</td>
//               <td>{c.position}</td>
//               {/* <td>{c.status}</td> */}
//               <td>
//                       <span
//                         style={{
//                           padding: '4px 8px',
//                           borderRadius: 4,
//                           color: '#fff',
//                           background:
//                             c.status === 'invitation_sent'
//                               ? '#28a745'
//                               : c.status === 'scheduled'
//                               ? '#ffc107'
//                               : '#6c757d'
//                         }}
//                       >
//                         {c.status}
//                       </span>
//                     </td>

//               <td>
//                 <button
//                   style={styles.button}
//                   onClick={() => openModal(c)}
//                 >
//                   Schedule Interview
//                 </button>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* ================= SCHEDULE INTERVIEW MODAL ================= */}

//       {showModal && (
//         <div style={styles.backdrop}>
//           <div style={styles.modal}>
//             <h3>Schedule Interview</h3>

//             <label>Date & Time</label>
//             <input
//               type="datetime-local"
//               style={styles.input}
//               onChange={e =>
//                 setForm({ ...form, scheduled_date: e.target.value })
//               }
//             />

//             <label>Interview Type</label>
//             <select
//               style={styles.input}
//               onChange={e =>
//                 setForm({ ...form, interview_type: e.target.value })
//               }
//             >
//               <option>Technical</option>
//               <option>Non-Technical</option>
//             </select>

//             <label>Department</label>
//             <select
//               style={styles.input}
//               onChange={e =>
//                 setForm({ ...form, interviewer_department: e.target.value })
//               }
//             >
//               <option value="">Select Department</option>
//               {POSITIONS.filter(p => p !== 'All').map(p => (
//                 <option key={p} value={p}>{p}</option>
//               ))}
//             </select>

//             <label>Interviewer</label>
//             <select
//               style={styles.input}
//               onChange={e => {
//                 const interviewer = interviewers.find(
//                   i => i.id === Number(e.target.value)
//                 );
//                 if (interviewer) {
//                   setForm({
//                     ...form,
//                     interviewer_name: interviewer.name,
//                     interviewer_email: interviewer.email,
//                     interviewer_role: interviewer.role,
//                     interviewer_department: interviewer.department
//                   });
//                 }
//               }}
//             >
//               <option value="">Select Interviewer</option>
//               {interviewers.map(i => (
//                 <option key={i.id} value={i.id}>{i.name}</option>
//               ))}
//             </select>

//             <button style={styles.confirmBtn} onClick={scheduleInterview}>
//               Confirm
//             </button>
//             <button
//               style={styles.cancelBtn}
//               onClick={() => setShowModal(false)}
//             >
//               Cancel
//             </button>
//           </div>
//         </div>
//       )}

//       {/* ================= MANAGE INTERVIEWERS MODAL ================= */}

//       {showInterviewerModal && (
//         <div style={styles.backdrop}>
//           <div style={{ ...styles.modal, width: 650, maxHeight: '80vh', overflowY: 'auto' }}>
//             <h3>Manage Interviewers</h3>

//             <input
//               placeholder="Name"
//               style={styles.input}
//               value={newInterviewer.name}
//               onChange={e =>
//                 setNewInterviewer({ ...newInterviewer, name: e.target.value })
//               }
//             />

//             <input
//               placeholder="Email"
//               style={styles.input}
//               value={newInterviewer.email}
//               onChange={e =>
//                 setNewInterviewer({ ...newInterviewer, email: e.target.value })
//               }
//             />

//             <select
//               style={styles.input}
//               value={newInterviewer.role}
//               onChange={e =>
//                 setNewInterviewer({ ...newInterviewer, role: e.target.value })
//               }
//             >
//               <option value="">Select Role</option>
//               <option value="HR">HR</option>
//               <option value="Others">Others</option>
//             </select>

//             <select
//               style={styles.input}
//               value={newInterviewer.department}
//               onChange={e =>
//                 setNewInterviewer({ ...newInterviewer, department: e.target.value })
//               }
//             >
//               <option value="">Select Department</option>
//               {POSITIONS.filter(p => p !== 'All').map(p => (
//                 <option key={p} value={p}>{p}</option>
//               ))}
//             </select>

//             <button style={styles.confirmBtn} onClick={addInterviewer}>
//               Add Interviewer
//             </button>

//             <hr />

//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th>Name</th>
//                   <th>Email</th>
//                   <th>Role</th>
//                   <th>Department</th>
//                   <th>Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {interviewers.map(i => (
//                   <tr key={i.id}>
//                     <td>{i.name}</td>
//                     <td>{i.email || '-'}</td>
//                     <td>{i.role || '-'}</td>
//                     <td>{i.department || '-'}</td>
//                     <td>
//                       <button
//                         style={styles.deleteBtn}
//                         onClick={() => deleteInterviewer(i.id)}
//                       >
//                         Delete
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>

//             <button
//               style={styles.cancelBtn}
//               onClick={() => setShowInterviewerModal(false)}
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// /* ================= STYLES ================= */

// const styles = {
//   page: { padding: 20 },
//   header: { display: 'flex', justifyContent: 'space-between', marginBottom: 10 },
//   select: { padding: 6, marginRight: 10 },
//   manageBtn: { padding: '6px 12px', background: '#ff9800', color: '#fff', border: 'none' },
//   table: { width: '100%', marginTop: 10, borderCollapse: 'collapse' },
//   button: { background: '#007bff', color: '#fff', padding: '6px 10px', border: 'none' },
//   backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)' },
//   modal: { background: '#fff', padding: 20, width: 420, margin: 'auto' },
//   input: { width: '100%', padding: 6, marginBottom: 8 },
//   confirmBtn: { background: '#28a745', color: '#fff', padding: 6, border: 'none' },
//   cancelBtn: { background: '#6c757d', color: '#fff', padding: 6, border: 'none', marginTop: 8 },
//   deleteBtn: { background: '#dc3545', color: '#fff', padding: '4px 8px', border: 'none' }
// };


// // import React, { useEffect, useState } from 'react';
// // import {
// //   candidatesAPI,
// //   interviewsAPI,
// //   emailsAPI,
// //   interviewersAPI
// // } from '../api/client';
// // import { toast } from 'react-toastify';

// // const POSITIONS = [
// //   'All',
// //   'Embedded System',
// //   'Embedded Software',
// //   'Post Silicon Validation',
// //   'VLSI',
// //   'Others',
// //   'HR',
// //   'Business Unit'
// // ];

// // export default function InterviewsList() {
// //   /* ================= STATES ================= */

// //   const [candidates, setCandidates] = useState([]);
// //   const [filtered, setFiltered] = useState([]);
// //   const [selectedPosition, setSelectedPosition] = useState('All');

// //   const [showModal, setShowModal] = useState(false);
// //   const [selectedCandidate, setSelectedCandidate] = useState(null);

// //   const [interviewers, setInterviewers] = useState([]);
// //   const [showInterviewerModal, setShowInterviewerModal] = useState(false);
// //   const [newInterviewer, setNewInterviewer] = useState('');

// //   const [form, setForm] = useState({
// //     scheduled_date: '',
// //     interview_type: 'Technical',
// //     interviewer_id: ''
// //   });

// //   /* ================= LOAD ================= */

// //   useEffect(() => {
// //     fetchCandidates();
// //     fetchInterviewers();
// //   }, []);

// //   useEffect(() => {
// //     filterByPosition();
// //   }, [selectedPosition, candidates]);

// //   /* ================= API ================= */

// //   const fetchCandidates = async () => {
// //     try {
// //       const res = await candidatesAPI.getAll();
// //       setCandidates(res.data);
// //       setFiltered(res.data);
// //     } catch {
// //       toast.error('Failed to fetch candidates');
// //     }
// //   };

// //   const fetchInterviewers = async () => {
// //     try {
// //       const res = await interviewersAPI.getAll();
// //       setInterviewers(res.data);
// //     } catch {
// //       toast.error('Failed to load interviewers');
// //     }
// //   };

// //   /* ================= FILTER ================= */

// //   const filterByPosition = () => {
// //     if (selectedPosition === 'All') {
// //       setFiltered(candidates);
// //       return;
// //     }

// //     setFiltered(
// //       candidates.filter(c =>
// //         (c.position || '')
// //           .toLowerCase()
// //           .includes(selectedPosition.toLowerCase())
// //       )
// //     );
// //   };

// //   /* ================= INTERVIEW MODAL ================= */

// //   const openModal = (candidate) => {
// //     setSelectedCandidate(candidate);
// //     setForm({
// //       scheduled_date: '',
// //       interview_type: 'Technical',
// //       interviewer_id: ''
// //     });
// //     setShowModal(true);
// //   };

// //   const scheduleInterview = async () => {
// //     if (!form.scheduled_date || !form.interviewer_id) {
// //       toast.error('Please fill all fields');
// //       return;
// //     }

// //     try {
// //       const interviewRes = await interviewsAPI.create({
// //         candidate_id: selectedCandidate.id,
// //         scheduled_date: form.scheduled_date,
// //         interview_type: form.interview_type,
// //         interviewer_id: form.interviewer_id
// //       });

// //       await emailsAPI.sendInterview({
// //         candidate_email: selectedCandidate.email_id,
// //         candidate_id: selectedCandidate.id,
// //         interview_id: interviewRes.data.id,
// //         email_service: 'gmail'
// //       });

// //       toast.success('Interview scheduled');
// //       setShowModal(false);
// //     } catch {
// //       toast.error('Scheduling failed');
// //     }
// //   };

// //   /* ================= INTERVIEWER MANAGEMENT ================= */

// //   const addInterviewer = async () => {
// //     if (!newInterviewer.trim()) {
// //       toast.error('Enter interviewer name');
// //       return;
// //     }

// //     await interviewersAPI.create({ name: newInterviewer });
// //     setNewInterviewer('');
// //     fetchInterviewers();
// //     toast.success('Interviewer added');
// //   };

// //   const deleteInterviewer = async (id) => {
// //     await interviewersAPI.delete(id);
// //     fetchInterviewers();
// //     toast.success('Interviewer removed');
// //   };

// //   /* ================= UI ================= */

// //   return (
// //     <div style={styles.page}>
// //       <style>{keyframes}</style>

// //       {/* HEADER */}
// //       <div style={styles.header}>
// //         <h2>Interviews</h2>

// //         <div>
// //           <select
// //             style={styles.select}
// //             value={selectedPosition}
// //             onChange={e => setSelectedPosition(e.target.value)}
// //           >
// //             {POSITIONS.map(p => (
// //               <option key={p}>{p}</option>
// //             ))}
// //           </select>

// //           <button
// //             style={styles.manageBtn}
// //             onClick={() => setShowInterviewerModal(true)}
// //           >
// //             Manage Interviewers
// //           </button>
// //         </div>
// //       </div>

// //       {/* TABLE */}
// //       <table style={styles.table}>
// //         <thead>
// //           <tr>
// //             <th>Name</th>
// //             <th>Position</th>
// //             <th>Status</th>
// //             <th>Action</th>
// //           </tr>
// //         </thead>
// //         <tbody>
// //           {filtered.map(c => (
// //             <tr key={c.id}>
// //               <td>{c.first_name} {c.last_name}</td>
// //               <td>{c.position}</td>
// //               <td>{c.status}</td>
// //               <td>
// //                 <button style={styles.button} onClick={() => openModal(c)}>
// //                   Schedule Interview
// //                 </button>
// //               </td>
// //             </tr>
// //           ))}
// //         </tbody>
// //       </table>

// //       {/* SCHEDULE MODAL */}
// //       {showModal && (
// //         <div style={styles.backdrop}>
// //           <div style={styles.modal}>
// //             <h3>Schedule Interview</h3>

// //             <input
// //               type="datetime-local"
// //               style={styles.input}
// //               onChange={e =>
// //                 setForm({ ...form, scheduled_date: e.target.value })
// //               }
// //             />

// //             <select
// //               style={styles.input}
// //               onChange={e =>
// //                 setForm({ ...form, interview_type: e.target.value })
// //               }
// //             >
// //               <option>Technical</option>
// //               <option>Non-Technical</option>
// //             </select>

// //             <select
// //               style={styles.input}
// //               onChange={e =>
// //                 setForm({ ...form, interviewer_id: e.target.value })
// //               }
// //             >
// //               <option value="">Select Interviewer</option>
// //               {interviewers.map(i => (
// //                 <option key={i.id} value={i.id}>{i.name}</option>
// //               ))}
// //             </select>

// //             <button style={styles.confirmBtn} onClick={scheduleInterview}>
// //               Confirm
// //             </button>
// //             <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
// //               Cancel
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* INTERVIEWER POPUP */}
// //       {showInterviewerModal && (
// //         <div style={styles.backdrop}>
// //           <div style={styles.modal}>
// //             <h3>Manage Interviewers</h3>

// //             <input
// //               placeholder="Interviewer name"
// //               style={styles.input}
// //               value={newInterviewer}
// //               onChange={e => setNewInterviewer(e.target.value)}
// //             />
// //             <button style={styles.confirmBtn} onClick={addInterviewer}>
// //               Add
// //             </button>

// //             <hr />

// //             {interviewers.map(i => (
// //               <div key={i.id} style={styles.listRow}>
// //                 {i.name}
// //                 <button
// //                   style={styles.deleteBtn}
// //                   onClick={() => deleteInterviewer(i.id)}
// //                 >
// //                   Delete
// //                 </button>
// //               </div>
// //             ))}

// //             <button
// //               style={styles.cancelBtn}
// //               onClick={() => setShowInterviewerModal(false)}
// //             >
// //               Close
// //             </button>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }

// // /* ================= STYLES ================= */

// // const styles = {
// //   page: { padding: 20 },
// //   header: { display: 'flex', justifyContent: 'space-between' },
// //   select: { padding: 6, marginRight: 10 },
// //   manageBtn: { padding: '6px 12px', background: '#ff9800', color: '#fff' },
// //   table: { width: '100%', marginTop: 10 },
// //   button: { background: '#007bff', color: '#fff', padding: '6px 10px' },
// //   backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)' },
// //   modal: { background: '#fff', padding: 20, width: 400, margin: 'auto' },
// //   input: { width: '100%', padding: 6, marginBottom: 8 },
// //   confirmBtn: { background: '#28a745', color: '#fff', padding: 6 },
// //   cancelBtn: { background: '#6c757d', color: '#fff', padding: 6 },
// //   deleteBtn: { background: '#dc3545', color: '#fff', padding: '4px 8px' },
// //   listRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 }
// // };

// // const keyframes = ``;

// // // import React, { useEffect, useState } from 'react';
// // // import {
// // //   candidatesAPI,
// // //   interviewsAPI,
// // //   emailsAPI,
// // //   interviewersAPI   // ✅ NEW
// // // } from '../api/client';
// // // import { toast } from 'react-toastify';

// // // const POSITIONS = [
// // //   'All',
// // //   'Embedded System',
// // //   'Embedded Software',
// // //   'Post Silicon Validation',
// // //   'VLSI',
// // //   'Others',
// // //   'HR',
// // //   'Business Unit'
// // // ];

// // // export default function InterviewsList() {
// // //   /* ================= STATES ================= */

// // //   const [candidates, setCandidates] = useState([]);
// // //   const [filtered, setFiltered] = useState([]);
// // //   const [selectedPosition, setSelectedPosition] = useState('All');

// // //   const [showModal, setShowModal] = useState(false);
// // //   const [selectedCandidate, setSelectedCandidate] = useState(null);

// // //   const [interviewers, setInterviewers] = useState([]); // ✅ NEW

// // //   const [form, setForm] = useState({
// // //     scheduled_date: '',
// // //     interview_type: 'Technical',
// // //     interviewer_id: '' // ✅ CHANGED (id instead of name)
// // //   });

// // //   /* ================= LOAD ================= */

// // //   useEffect(() => {
// // //     console.log('🔁 Interviews page loaded');
// // //     fetchCandidates();
// // //     fetchInterviewers(); // ✅ NEW
// // //   }, []);

// // //   useEffect(() => {
// // //     filterByPosition();
// // //   }, [selectedPosition, candidates]);

// // //   /* ================= API CALLS ================= */

// // //   const fetchCandidates = async () => {
// // //     try {
// // //       console.log('📡 Fetching candidates...');
// // //       const res = await candidatesAPI.getAll();
// // //       console.log('✅ Candidates:', res.data);
// // //       setCandidates(res.data);
// // //       setFiltered(res.data);
// // //     } catch (err) {
// // //       console.error('❌ Candidate fetch failed', err);
// // //       toast.error('Failed to fetch candidates');
// // //     }
// // //   };

// // //   const fetchInterviewers = async () => {
// // //     try {
// // //       console.log('📡 Fetching interviewers...');
// // //       const res = await interviewersAPI.getAll();
// // //       console.log('✅ Interviewers:', res.data);
// // //       setInterviewers(res.data);
// // //     } catch (err) {
// // //       console.error('❌ Interviewer fetch failed', err);
// // //       toast.error('Failed to load interviewers');
// // //     }
// // //   };

// // //   /* ================= FILTER ================= */

// // //   const filterByPosition = () => {
// // //     console.log('🔍 Filter position:', selectedPosition);

// // //     if (selectedPosition === 'All') {
// // //       setFiltered(candidates);
// // //       return;
// // //     }

// // //     const data = candidates.filter(c =>
// // //       (c.position || '')
// // //         .toLowerCase()
// // //         .includes(selectedPosition.toLowerCase())
// // //     );

// // //     setFiltered(data);
// // //   };

// // //   /* ================= MODAL ================= */

// // //   const openModal = (candidate) => {
// // //     console.log('🟢 Schedule clicked:', candidate);

// // //     setSelectedCandidate(candidate);
// // //     setForm({
// // //       scheduled_date: '',
// // //       interview_type: 'Technical',
// // //       interviewer_id: ''
// // //     });

// // //     setShowModal(true);
// // //   };

// // //   /* ================= SCHEDULE ================= */

// // //   const scheduleInterview = async () => {
// // //     console.log('🚀 Confirm schedule clicked');

// // //     if (
// // //       !form.scheduled_date ||
// // //       !form.interviewer_id
// // //     ) {
// // //       toast.error('Please fill all fields');
// // //       return;
// // //     }

// // //     const payload = {
// // //       candidate_id: selectedCandidate.id,
// // //       scheduled_date: form.scheduled_date,
// // //       interview_type: form.interview_type,
// // //       interviewer_id: form.interviewer_id
// // //     };

// // //     console.log('📦 Interview payload:', payload);

// // //     try {
// // //       const interviewRes = await interviewsAPI.create(payload);
// // //       console.log('✅ Interview created:', interviewRes.data);

// // //       await emailsAPI.sendInterview({
// // //         candidate_email: selectedCandidate.email_id,
// // //         candidate_id: selectedCandidate.id,
// // //         interview_id: interviewRes.data.id,
// // //         email_service: 'gmail'
// // //       });

// // //       console.log('📧 Email sent');
// // //       toast.success('Interview scheduled & email sent');
// // //       setShowModal(false);
// // //     } catch (err) {
// // //       console.error('❌ Schedule failed', err);
// // //       toast.error('Failed to schedule interview');
// // //     }
// // //   };

// // //   /* ================= UI ================= */

// // //   return (
// // //     <div style={styles.page}>
// // //       <style>{keyframes}</style>

// // //       <div style={styles.header}>
// // //         <h2 style={styles.title}>
// // //           Interviews {selectedPosition !== 'All' && `– ${selectedPosition}`}
// // //         </h2>

// // //         <select
// // //           style={styles.select}
// // //           value={selectedPosition}
// // //           onChange={e => setSelectedPosition(e.target.value)}
// // //         >
// // //           {POSITIONS.map(p => (
// // //             <option key={p}>{p}</option>
// // //           ))}
// // //         </select>
// // //       </div>

// // //       <table style={styles.table}>
// // //         <thead>
// // //           <tr>
// // //             <th>Name</th>
// // //             <th>Position</th>
// // //             <th>Status</th>
// // //             <th>Action</th>
// // //           </tr>
// // //         </thead>

// // //         <tbody>
// // //           {filtered.map(c => (
// // //             <tr key={c.id} style={styles.row}>
// // //               <td>{c.first_name} {c.last_name}</td>
// // //               <td>{c.position}</td>
// // //               <td>{c.status}</td>
// // //               <td>
// // //                 <button
// // //                   style={styles.button}
// // //                   onClick={() => openModal(c)}
// // //                 >
// // //                   Schedule Interview
// // //                 </button>
// // //               </td>
// // //             </tr>
// // //           ))}
// // //         </tbody>
// // //       </table>

// // //       {/* ================= MODAL ================= */}

// // //       {showModal && (
// // //         <div style={styles.backdrop}>
// // //           <div style={styles.modal}>
// // //             <h3>Schedule Interview</h3>

// // //             <p><b>Name:</b> {selectedCandidate.first_name}</p>
// // //             <p><b>Position:</b> {selectedCandidate.position}</p>

// // //             <label>Date & Time</label>
// // //             <input
// // //               type="datetime-local"
// // //               style={styles.input}
// // //               value={form.scheduled_date}
// // //               onChange={e =>
// // //                 setForm({ ...form, scheduled_date: e.target.value })
// // //               }
// // //             />

// // //             <label>Interview Type</label>
// // //             <select
// // //               style={styles.input}
// // //               value={form.interview_type}
// // //               onChange={e =>
// // //                 setForm({ ...form, interview_type: e.target.value })
// // //               }
// // //             >
// // //               <option>Technical</option>
// // //               <option>Non-Technical</option>
// // //             </select>

// // //             <label>Interviewer</label>
// // //             <select
// // //               style={styles.input}
// // //               value={form.interviewer_id}
// // //               onChange={e =>
// // //                 setForm({ ...form, interviewer_id: e.target.value })
// // //               }
// // //             >
// // //               <option value="">Select Interviewer</option>
// // //               {interviewers.map(i => (
// // //                 <option key={i.id} value={i.id}>
// // //                   {i.name}
// // //                 </option>
// // //               ))}
// // //             </select>

// // //             <div style={styles.modalActions}>
// // //               <button
// // //                 style={styles.confirmBtn}
// // //                 onClick={scheduleInterview}
// // //               >
// // //                 Confirm & Send Email
// // //               </button>

// // //               <button
// // //                 style={styles.cancelBtn}
// // //                 onClick={() => setShowModal(false)}
// // //               >
// // //                 Cancel
// // //               </button>
// // //             </div>
// // //           </div>
// // //         </div>
// // //       )}
// // //     </div>
// // //   );
// // // }

// // // /* ================= STYLES ================= */

// // // const styles = {
// // //   page: { padding: 20 },

// // //   header: {
// // //     display: 'flex',
// // //     justifyContent: 'space-between',
// // //     marginBottom: 15
// // //   },

// // //   title: { margin: 0 },
// // //   select: { padding: 6 },

// // //   table: {
// // //     width: '100%',
// // //     borderCollapse: 'collapse'
// // //   },

// // //   row: {
// // //     transition: 'background 0.3s'
// // //   },

// // //   button: {
// // //     padding: '6px 12px',
// // //     background: '#007bff',
// // //     color: '#fff',
// // //     border: 'none',
// // //     borderRadius: 4,
// // //     cursor: 'pointer'
// // //   },

// // //   backdrop: {
// // //     position: 'fixed',
// // //     inset: 0,
// // //     background: 'rgba(0,0,0,0.5)',
// // //     display: 'flex',
// // //     alignItems: 'center',
// // //     justifyContent: 'center',
// // //     animation: 'fadeIn 0.3s'
// // //   },

// // //   modal: {
// // //     background: '#fff',
// // //     padding: 20,
// // //     width: 400,
// // //     borderRadius: 10,
// // //     animation: 'slideUp 0.3s'
// // //   },

// // //   input: {
// // //     width: '100%',
// // //     padding: 6,
// // //     marginTop: 6,
// // //     marginBottom: 10
// // //   },

// // //   modalActions: {
// // //     display: 'flex',
// // //     gap: 10
// // //   },

// // //   confirmBtn: {
// // //     background: '#28a745',
// // //     color: '#fff',
// // //     padding: '6px 12px',
// // //     border: 'none',
// // //     borderRadius: 4
// // //   },

// // //   cancelBtn: {
// // //     background: '#6c757d',
// // //     color: '#fff',
// // //     padding: '6px 12px',
// // //     border: 'none',
// // //     borderRadius: 4
// // //   }
// // // };

// // // /* ================= KEYFRAMES ================= */

// // // const keyframes = `
// // // @keyframes fadeIn {
// // //   from { opacity: 0 }
// // //   to { opacity: 1 }
// // // }

// // // @keyframes slideUp {
// // //   from { transform: translateY(20px); opacity: 0 }
// // //   to { transform: translateY(0); opacity: 1 }
// // // }
// // // `;

// // // // import React, { useEffect, useState } from 'react';
// // // // import { candidatesAPI, interviewsAPI, emailsAPI } from '../api/client';
// // // // import { toast } from 'react-toastify';

// // // // const POSITIONS = [
// // // //   'All',
// // // //   'Embedded System',
// // // //   'Embedded Software',
// // // //   'Post Silicon Validation',
// // // //   'VLSI',
// // // //   'Others',
// // // //   'HR',
// // // //   'Business Unit'
// // // // ];

// // // // export default function InterviewsList() {
// // // //   const [candidates, setCandidates] = useState([]);
// // // //   const [filtered, setFiltered] = useState([]);
// // // //   const [selectedPosition, setSelectedPosition] = useState('All');

// // // //   const [showModal, setShowModal] = useState(false);
// // // //   const [selectedCandidate, setSelectedCandidate] = useState(null);

// // // //   const [form, setForm] = useState({
// // // //     scheduled_date: '',
// // // //     interview_type: 'Technical',
// // // //     interviewer_name: ''
// // // //   });

// // // //   /* ================= LOAD ================= */
// // // //   useEffect(() => {
// // // //     console.log('🔁 Interviews page loaded');
// // // //     fetchCandidates();
// // // //   }, []);

// // // //   useEffect(() => {
// // // //     filterByPosition();
// // // //   }, [selectedPosition, candidates]);

// // // //   const fetchCandidates = async () => {
// // // //     try {
// // // //       console.log('📡 Fetching candidates...');
// // // //       const res = await candidatesAPI.getAll();
// // // //       console.log('✅ Candidates:', res.data);
// // // //       setCandidates(res.data);
// // // //       setFiltered(res.data);
// // // //     } catch (err) {
// // // //       console.error('❌ Candidate fetch failed', err);
// // // //       toast.error('Failed to fetch candidates');
// // // //     }
// // // //   };

// // // //   /* ================= FILTER ================= */
// // // //   const filterByPosition = () => {
// // // //     console.log('🔍 Filter position:', selectedPosition);

// // // //     if (selectedPosition === 'All') {
// // // //       setFiltered(candidates);
// // // //       return;
// // // //     }

// // // //     const data = candidates.filter(c =>
// // // //       (c.position || '').toLowerCase().includes(selectedPosition.toLowerCase())
// // // //     );

// // // //     setFiltered(data);
// // // //   };

// // // //   /* ================= MODAL ================= */
// // // //   const openModal = (candidate) => {
// // // //     console.log('🟢 Schedule clicked:', candidate);
// // // //     setSelectedCandidate(candidate);
// // // //     setForm({
// // // //       scheduled_date: '',
// // // //       interview_type: 'Technical',
// // // //       interviewer_name: ''
// // // //     });
// // // //     setShowModal(true);
// // // //   };

// // // //   const scheduleInterview = async () => {
// // // //     console.log('🚀 Confirm schedule clicked');

// // // //     if (!form.scheduled_date || !form.interviewer_name) {
// // // //       toast.error('Please fill all fields');
// // // //       return;
// // // //     }

// // // //     const payload = {
// // // //       candidate_id: selectedCandidate.id,
// // // //       scheduled_date: form.scheduled_date,
// // // //       interview_type: form.interview_type,
// // // //       interviewer_name: form.interviewer_name
// // // //     };

// // // //     console.log('📦 Interview payload:', payload);

// // // //     try {
// // // //       const interviewRes = await interviewsAPI.create(payload);
// // // //       console.log('✅ Interview created:', interviewRes.data);

// // // //       await emailsAPI.sendInterview({
// // // //         candidate_email: selectedCandidate.email_id,   // ✅ FIX
// // // //         candidate_id: selectedCandidate.id,
// // // //         interview_id: interviewRes.data.id,
// // // //         email_service: 'gmail'
// // // //       });

// // // //       console.log('📧 Email sent');
// // // //       toast.success('Interview scheduled & email sent');
// // // //       setShowModal(false);
// // // //     } catch (err) {
// // // //       console.error('❌ Schedule failed', err);
// // // //       toast.error('Failed to schedule interview');
// // // //     }
// // // //   };

// // // //   /* ================= UI ================= */
// // // //   return (
// // // //     <div style={styles.page}>
// // // //       {/* Inline animation styles */}
// // // //       <style>{keyframes}</style>

// // // //       <div style={styles.header}>
// // // //         <h2 style={styles.title}>
// // // //           Interviews {selectedPosition !== 'All' && `– ${selectedPosition}`}
// // // //         </h2>

// // // //         <select
// // // //           style={styles.select}
// // // //           value={selectedPosition}
// // // //           onChange={e => setSelectedPosition(e.target.value)}
// // // //         >
// // // //           {POSITIONS.map(p => (
// // // //             <option key={p}>{p}</option>
// // // //           ))}
// // // //         </select>
// // // //       </div>

// // // //       <table style={styles.table}>
// // // //         <thead>
// // // //           <tr>
// // // //             <th>Name</th>
// // // //             <th>Position</th>
// // // //             <th>Status</th>
// // // //             <th>Action</th>
// // // //           </tr>
// // // //         </thead>
// // // //         <tbody>
// // // //           {filtered.map(c => (
// // // //             <tr key={c.id} style={styles.row}>
// // // //               <td>{c.first_name} {c.last_name}</td>
// // // //               <td>{c.position}</td>
// // // //               <td>{c.status}</td>
// // // //               <td>
// // // //                 <button
// // // //                   style={styles.button}
// // // //                   onClick={() => openModal(c)}
// // // //                 >
// // // //                   Schedule Interview
// // // //                 </button>
// // // //               </td>
// // // //             </tr>
// // // //           ))}
// // // //         </tbody>
// // // //       </table>

// // // //       {/* ================= MODAL ================= */}
// // // //       {showModal && (
// // // //         <div style={styles.backdrop}>
// // // //           <div style={styles.modal}>
// // // //             <h3>Schedule Interview</h3>

// // // //             <p><b>Name:</b> {selectedCandidate.first_name}</p>
// // // //             <p><b>Position:</b> {selectedCandidate.position}</p>

// // // //             <label>Date & Time</label>
// // // //             <input
// // // //               type="datetime-local"
// // // //               style={styles.input}
// // // //               onChange={e => {
// // // //                 console.log('📅 Date:', e.target.value);
// // // //                 setForm({ ...form, scheduled_date: e.target.value });
// // // //               }}
// // // //             />

// // // //             <label>Interview Type</label>
// // // //             <select
// // // //               style={styles.input}
// // // //               onChange={e => setForm({ ...form, interview_type: e.target.value })}
// // // //             >
// // // //               <option>Technical</option>
// // // //               <option>Non-Technical</option>
// // // //             </select>

// // // //             <label>Interviewer Name</label>
// // // //             <input
// // // //               placeholder="Enter interviewer name"
// // // //               style={styles.input}
// // // //               onChange={e => setForm({ ...form, interviewer_name: e.target.value })}
// // // //             />

// // // //             <div style={styles.modalActions}>
// // // //               <button style={styles.confirmBtn} onClick={scheduleInterview}>
// // // //                 Confirm & Send Email
// // // //               </button>
// // // //               <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
// // // //                 Cancel
// // // //               </button>
// // // //             </div>
// // // //           </div>
// // // //         </div>
// // // //       )}
// // // //     </div>
// // // //   );
// // // // }

// // // // /* ================= INLINE STYLES ================= */

// // // // const styles = {
// // // //   page: { padding: 20 },
// // // //   header: {
// // // //     display: 'flex',
// // // //     justifyContent: 'space-between',
// // // //     marginBottom: 15
// // // //   },
// // // //   title: { margin: 0 },
// // // //   select: { padding: 6 },

// // // //   table: {
// // // //     width: '100%',
// // // //     borderCollapse: 'collapse'
// // // //   },
// // // //   row: {
// // // //     transition: 'background 0.3s'
// // // //   },
// // // //   button: {
// // // //     padding: '6px 12px',
// // // //     background: '#007bff',
// // // //     color: '#fff',
// // // //     border: 'none',
// // // //     borderRadius: 4,
// // // //     cursor: 'pointer',
// // // //     transition: 'transform 0.2s, background 0.3s'
// // // //   },

// // // //   backdrop: {
// // // //     position: 'fixed',
// // // //     inset: 0,
// // // //     background: 'rgba(0,0,0,0.5)',
// // // //     display: 'flex',
// // // //     alignItems: 'center',
// // // //     justifyContent: 'center',
// // // //     animation: 'fadeIn 0.3s'
// // // //   },

// // // //   modal: {
// // // //     background: '#fff',
// // // //     padding: 20,
// // // //     width: 400,
// // // //     borderRadius: 10,
// // // //     animation: 'slideUp 0.3s'
// // // //   },

// // // //   input: {
// // // //     width: '100%',
// // // //     padding: 6,
// // // //     marginTop: 6,
// // // //     marginBottom: 10
// // // //   },

// // // //   modalActions: {
// // // //     display: 'flex',
// // // //     gap: 10
// // // //   },

// // // //   confirmBtn: {
// // // //     background: '#28a745',
// // // //     color: '#fff',
// // // //     padding: '6px 12px',
// // // //     border: 'none',
// // // //     borderRadius: 4,
// // // //     cursor: 'pointer'
// // // //   },

// // // //   cancelBtn: {
// // // //     background: '#6c757d',
// // // //     color: '#fff',
// // // //     padding: '6px 12px',
// // // //     border: 'none',
// // // //     borderRadius: 4,
// // // //     cursor: 'pointer'
// // // //   }
// // // // };

// // // // /* ================= KEYFRAMES ================= */

// // // // const keyframes = `
// // // // @keyframes fadeIn {
// // // //   from { opacity: 0 }
// // // //   to { opacity: 1 }
// // // // }

// // // // @keyframes slideUp {
// // // //   from { transform: translateY(20px); opacity: 0 }
// // // //   to { transform: translateY(0); opacity: 1 }
// // // // }
// // // // `;

// // // // // import React, { useEffect, useState } from 'react';
// // // // // import { candidatesAPI, interviewsAPI, emailsAPI } from '../api/client';
// // // // // import { toast } from 'react-toastify';

// // // // // export default function InterviewsList() {
// // // // //   const [candidates, setCandidates] = useState([]);
// // // // //   const [selected, setSelected] = useState(null);
// // // // //   const [showModal, setShowModal] = useState(false);

// // // // //   const [form, setForm] = useState({
// // // // //     scheduled_date: '',
// // // // //     interview_type: 'Technical',
// // // // //     interviewer_name: ''
// // // // //   });

// // // // //   useEffect(() => {
// // // // //     fetchCandidates();
// // // // //   }, []);

// // // // //   // ================= FETCH CANDIDATES =================
// // // // //   const fetchCandidates = async () => {
// // // // //     try {
// // // // //       const res = await candidatesAPI.getAll();
// // // // //       setCandidates(res.data);
// // // // //     } catch {
// // // // //       toast.error('Failed to fetch candidates');
// // // // //     }
// // // // //   };

// // // // //   // ================= OPEN MODAL =================
// // // // //   const openSchedule = (candidate) => {
// // // // //     setSelected(candidate);
// // // // //     setShowModal(true);
// // // // //   };

// // // // //   // ================= SCHEDULE INTERVIEW =================
// // // // //   const handleSchedule = async () => {
// // // // //     if (!form.scheduled_date || !form.interviewer_name) {
// // // // //       toast.error('Please fill all fields');
// // // // //       return;
// // // // //     }

// // // // //     try {
// // // // //       // 1️⃣ Create Interview
// // // // //       const interviewRes = await interviewsAPI.create({
// // // // //         candidate_id: selected.id,
// // // // //         scheduled_date: form.scheduled_date,
// // // // //         interview_type: form.interview_type,
// // // // //         interviewer_name: form.interviewer_name
// // // // //       });

// // // // //       // 2️⃣ Send Email
// // // // //       await emailsAPI.sendInterview({
// // // // //         candidate_id: selected.id,
// // // // //         interview_id: interviewRes.data.id,
// // // // //         email_service: 'gmail'
// // // // //       });

// // // // //       toast.success('Interview scheduled & email sent');
// // // // //       setShowModal(false);
// // // // //       setForm({
// // // // //         scheduled_date: '',
// // // // //         interview_type: 'Technical',
// // // // //         interviewer_name: ''
// // // // //       });

// // // // //       fetchCandidates();
// // // // //     } catch {
// // // // //       toast.error('Failed to schedule interview');
// // // // //     }
// // // // //   };

// // // // //   // ================= UI =================
// // // // //   return (
// // // // //     <div className="card">
// // // // //       <h2>Interview Scheduling</h2>

// // // // //       <table className="table">
// // // // //         <thead>
// // // // //           <tr>
// // // // //             <th>Name</th>
// // // // //             <th>Position</th>
// // // // //             <th>Status</th>
// // // // //             <th>Action</th>
// // // // //           </tr>
// // // // //         </thead>
// // // // //         <tbody>
// // // // //           {candidates.map(c => (
// // // // //             <tr key={c.id}>
// // // // //               <td>{c.first_name} {c.last_name}</td>
// // // // //               <td>{c.position || '-'}</td>
// // // // //               <td>{c.status}</td>
// // // // //               <td>
// // // // //                 <button
// // // // //                   className="btn btn-primary"
// // // // //                   onClick={() => openSchedule(c)}
// // // // //                 >
// // // // //                   Schedule Interview
// // // // //                 </button>
// // // // //               </td>
// // // // //             </tr>
// // // // //           ))}
// // // // //         </tbody>
// // // // //       </table>

// // // // //       {/* ================= MODAL ================= */}
// // // // //       {showModal && (
// // // // //         <div className="modal-backdrop">
// // // // //           <div className="modal">
// // // // //             <h3>Schedule Interview</h3>

// // // // //             <p><b>Candidate:</b> {selected.first_name}</p>
// // // // //             <p><b>Position:</b> {selected.position}</p>

// // // // //             <input
// // // // //               type="datetime-local"
// // // // //               value={form.scheduled_date}
// // // // //               onChange={e =>
// // // // //                 setForm({ ...form, scheduled_date: e.target.value })
// // // // //               }
// // // // //             />

// // // // //             <select
// // // // //               value={form.interview_type}
// // // // //               onChange={e =>
// // // // //                 setForm({ ...form, interview_type: e.target.value })
// // // // //               }
// // // // //             >
// // // // //               <option>Technical</option>
// // // // //               <option>HR</option>
// // // // //               <option>Managerial</option>
// // // // //             </select>

// // // // //             <input
// // // // //               placeholder="Interviewer Name"
// // // // //               value={form.interviewer_name}
// // // // //               onChange={e =>
// // // // //                 setForm({ ...form, interviewer_name: e.target.value })
// // // // //               }
// // // // //             />

// // // // //             <div className="modal-actions">
// // // // //               <button onClick={handleSchedule} className="btn btn-success">
// // // // //                 Confirm & Send Email
// // // // //               </button>
// // // // //               <button
// // // // //                 onClick={() => setShowModal(false)}
// // // // //                 className="btn btn-secondary"
// // // // //               >
// // // // //                 Cancel
// // // // //               </button>
// // // // //             </div>
// // // // //           </div>
// // // // //         </div>
// // // // //       )}
// // // // //     </div>
// // // // //   );
// // // // // }
